import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

type RowRole = 'link' | 'button';

interface RowInteraction {
  onClick: (event: React.MouseEvent) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  tabIndex: 0;
  role: RowRole;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
}

/**
 * A click on a row does nothing when it would destroy something the user just did: it landed
 * on (or inside) a control that owns it (a link, button, form control, switch, menu), or it
 * ended a text selection. Letting a real link handle its own click is what keeps
 * ctrl/middle-click opening a new tab.
 */
export function shouldIgnoreRowClick(event: React.MouseEvent): boolean {
  if (event.defaultPrevented) return true;
  // Only a control inside the clicked element counts: an in-place row is itself role="button",
  // and the row (an ancestor of its cells) must not make every click inside it a no-op.
  const control = (event.target as HTMLElement).closest(
    "a, button, input, select, textarea, label, summary, [contenteditable], [role='button'], [role='menu'], [role='switch'], [role='checkbox'], [role='radio']",
  );
  if (control && control !== event.currentTarget && event.currentTarget.contains(control))
    return true;
  return (window.getSelection()?.toString() ?? '') !== '';
}

/** Enter/Space activates the row only when the key was pressed on the row itself, not on a control inside it. */
function shouldIgnoreRowKey(event: React.KeyboardEvent): boolean {
  return (
    event.defaultPrevented ||
    event.target !== event.currentTarget ||
    (event.key !== 'Enter' && event.key !== ' ')
  );
}

function interaction(
  go: () => void,
  role: RowRole,
  label?: string,
  expanded?: boolean,
): RowInteraction {
  return {
    onClick: (event) => {
      if (!shouldIgnoreRowClick(event)) go();
    },
    onKeyDown: (event) => {
      if (shouldIgnoreRowKey(event)) return;
      event.preventDefault();
      go();
    },
    tabIndex: 0,
    role,
    'aria-label': label,
    'aria-expanded': expanded,
  };
}

/**
 * Props that make any element open `href` on click or Enter/Space. Calls `useNavigate`, so call it
 * only in a component rendered for linked rows: a table whose rows go nowhere then needs no
 * router context. Pair it with a real `<Link>` in the row's first cell.
 */
export function useRowLink(href: string, label?: string): RowInteraction {
  const navigate = useNavigate();
  return interaction(() => navigate(href), 'link', label);
}

/** The real `<Link>` in a linked row's first cell. */
export function RowAnchor({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link to={href} className="block">
      {children}
    </Link>
  );
}

/** The in-place sibling of `useRowLink`: the same guard and keys, but the row does something instead of navigating. Not a hook. */
export function rowActionProps(
  onActivate: () => void,
  label?: string,
  expanded?: boolean,
): RowInteraction {
  return interaction(onActivate, 'button', label, expanded);
}

interface CellProps {
  className: string;
  children: React.ReactNode;
}

type Target =
  | { href: string; onActivate?: undefined }
  | { onActivate: () => void; href?: undefined };

function LinkCell({ href, className, children }: CellProps & { href: string }) {
  return (
    <td className={className} onClick={useRowLink(href).onClick}>
      {children}
    </td>
  );
}

function ActionCell({ onActivate, className, children }: CellProps & { onActivate: () => void }) {
  return (
    <td className={className} onClick={rowActionProps(onActivate).onClick}>
      {children}
    </td>
  );
}

/** A table cell that opens `href` (or runs `onActivate`) on click. Split from the row so `useRouter` only runs for linked cells. */
export function ClickableCell({ href, onActivate, className, children }: CellProps & Target) {
  return href !== undefined ? (
    <LinkCell href={href} className={className}>
      {children}
    </LinkCell>
  ) : (
    <ActionCell onActivate={onActivate} className={className}>
      {children}
    </ActionCell>
  );
}

interface RowProps {
  rowLabel?: string;
  className: string;
  /** `--row-delay` for the row-in animation. */
  delay?: string;
  dataState?: 'selected' | false;
  testId?: string;
  rowId?: string;
  /** `aria-expanded` for an `onActivate` row that toggles a panel. */
  expanded?: boolean;
  children: React.ReactNode;
}

function Tr({
  rowLabel,
  className,
  delay,
  dataState,
  testId,
  rowId,
  children,
  props,
}: RowProps & { props: RowInteraction }) {
  // Cell clicks are wired per cell so the actions column can opt out; only the keyboard part lives on the row.
  const { onClick: _onClick, ...rowProps } = props;
  return (
    <tr
      className={className}
      style={delay ? ({ '--row-delay': delay } as React.CSSProperties) : undefined}
      data-state={dataState}
      data-testid={testId}
      data-row-id={rowId}
      {...rowProps}
      aria-label={rowLabel}
    >
      {children}
    </tr>
  );
}

function LinkRow({ href, ...row }: RowProps & { href: string }) {
  return <Tr {...row} props={useRowLink(href)} />;
}

function ActionRow({ onActivate, ...row }: RowProps & { onActivate: () => void }) {
  return <Tr {...row} props={rowActionProps(onActivate, undefined, row.expanded)} />;
}

/**
 * The clickable `<tr>`: tabbable, `role="link"` (or `button` for `onActivate`), Enter/Space
 * activates it. Clicks are handled by `ClickableCell`s so the actions column stays out.
 */
export function ClickableRow({ href, onActivate, ...row }: RowProps & Target) {
  return href !== undefined ? (
    <LinkRow href={href} {...row} />
  ) : (
    <ActionRow onActivate={onActivate} {...row} />
  );
}
